/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { trackPageMount } from "@/utils/analytics";
import { getDisclaimer, getEMD, getCMS, getNearbyProperties, updatePropertyViewCount } from "@/api/PDP";
import { buildEventLabel } from "./utils";
import { getPropertyType } from "@/utils/propCategory";
import CommercialRecommendedProperties from "./CommercialRecommendedProperties";
import CommercialHeroSection from "./hero";
import NarrativeBar from "./description/NarrativeBar";
import PropertyDescription from "./description/PropertyDescription";
import PropertySidebar from "./description/PropertySidebar";
import PropertyDetailsSection from "./PropertyDetailsSection";
import Disclaimer from "@/components/pdp/pdpComponents/Disclaimer";
import CommercialFeesContent from "./CommercialFeesContent";
import PropertyPhotoGallery from "./PropertyPhotoGallery";
import BrokerSpotlight from "./BrokerSpotlight";
import DueDiligenceVault from "./DueDiligenceVault";
import LocationMarketContext from "./location/LocationMarketContext";
import CommercialFAQSection from "./CommercialFAQSection";
import AuctionInformation from "./AuctionInformation";
import "./commercial-pdp.css";

/** Ads Carousel with left/right arrows — shows 2 ads at a time, scrolls by 1 */
const AdsCarousel = ({ ads }) => {
  const [startIdx, setStartIdx] = useState(0);
  const visibleCount = 2;
  const maxIdx = ads.length - visibleCount;

  const prev = () => setStartIdx((i) => Math.max(0, i - 1));
  const next = () => setStartIdx((i) => Math.min(maxIdx, i + 1));

  return (
    <div className="commercial-pdp__ads-carousel-wrapper">
      <button type="button" className="commercial-pdp__ads-arrow commercial-pdp__ads-arrow--left" onClick={prev} disabled={startIdx === 0} aria-label="Previous ad">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      <div className="commercial-pdp__ads-carousel-track">
        {ads.slice(startIdx, startIdx + visibleCount).map((ad, idx) => (
          <div className="commercial-pdp__ads-slot" key={startIdx + idx}>
            <a href={ad.redirectionURL || '#'} target="_blank" rel="noopener noreferrer">
              <img src={ad.imgUrl} alt={ad.name || 'Advertisement'} />
            </a>
          </div>
        ))}
      </div>
      <button type="button" className="commercial-pdp__ads-arrow commercial-pdp__ads-arrow--right" onClick={next} disabled={startIdx >= maxIdx} aria-label="Next ad">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
    </div>
  );
};

const CommercialPDP = ({ pdpInitData, isUserLoggedIn, propertyId, pageCmsData: pageCmsDataProp, openTourModalOnLoad, onTourModalOnLoadConsumed, onRegisterTourRequested }) => {
  const navigate = useNavigate();
  const latestBidCenterData = useSelector((state) => state.pdp?.latestBidCenterData);
  const bidCenterData = useSelector((state) => state.pdp?.bidCenterData);

  // Local state for data fetched via API (not in Redux for commercial page)
  const [disclaimer, setDisclaimer] = useState(null);
  const [emd, setEmd] = useState(null);
  const [cmsData, setCmsData] = useState(pageCmsDataProp || null);
  const [nearbyProperties, setNearbyProperties] = useState(null);

  const { pdpPropertyFees, pdpDisclaimer, pdpDocuments, pdpAds } = cmsData || {};
  const isFcOnlineFlag = pdpInitData?.fcOnlineFlag;
  const provisionalSaleDuration = pdpInitData?.listingDetail?.provisionalSaleDuration;

  const propertyDetails = pdpInitData?.propertyDetails;
  const propertySpecialDetails = pdpInitData?.propertySpecialDetails;
  const listingDetail = pdpInitData?.listingDetail;
  const address = propertyDetails?.address;
  const commonAttributes = propertyDetails?.commonAttributes;
  const propertyDocumentsInformation = pdpInitData?.propertyDocumentsInformation ?? null;

  // Use page-level CMS data when it arrives (if not already set from prop)
  useEffect(() => {
    if (pageCmsDataProp && !cmsData) {
      setCmsData(pageCmsDataProp);
    }
  }, [pageCmsDataProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect away from sold/deleted/under-contract properties
  useEffect(() => {
    if (pdpInitData?.propertyDetails?.propertyId !== propertyId) return;

    const status = pdpInitData?.propertyDetails?.commonAttributes?.status;
    const propertyTag = pdpInitData?.propertySpecialDetails?.propertyTag;
    const realbidStatus = pdpInitData?.propertyDetails?.detailedAttributes?.realbidPropertyStatus;
    const stateLandingUrl = pdpInitData?.propertyDetails?.address?.stateLandingUrl;

    if (status === 'SLD' || status === 'DELETED') {
      navigate(stateLandingUrl);
    } else if (status === 'REVIEW_PENDING' || status === 'INCOMPLETE' ||
               (propertyTag === 'UNDER_CONTRACT' &&
                (realbidStatus === 'REOS_RC' || realbidStatus === 'REOS_LC'))) {
      navigate(stateLandingUrl);
    }
  }, [pdpInitData?.propertyDetails?.commonAttributes?.status, pdpInitData?.propertyDetails?.propertyId, propertyId, navigate]);

  // Fetch CMS data only if not provided by parent (fallback)
  useEffect(() => {
    if (pageCmsDataProp) return; // Skip — already provided by parent
    let cancelled = false;
    getCMS("pages/pdp").then((response) => {
      if (!cancelled) {
        setCmsData(response?.results?.[0]?.sections);
      }
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch disclaimer and EMD (depends on property data)
  useEffect(() => {
    if (!pdpInitData?.propertyDetails?.propertyId) return;
    let cancelled = false;

    const fetchData = async () => {
      const propId = pdpInitData.propertyDetails.propertyId;
      const listingId = pdpInitData.propertyDetails.listingId;

      // Update view count (matches residential PDP behavior)
      updatePropertyViewCount(propId, listingId);

      const [disclaimerRes, emdRes] = await Promise.allSettled([
        getDisclaimer(propId),
        getEMD(listingId),
      ]);

      if (cancelled) return;

      if (disclaimerRes.status === "fulfilled") {
        setDisclaimer(disclaimerRes.value?.data);
      }
      if (emdRes.status === "fulfilled") {
        setEmd(emdRes.value?.data);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [pdpInitData?.propertyDetails?.propertyId]);

  // Fetch nearby properties
  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;

    const fetchNearbyProperties = async () => {
      try {
        const result = await getNearbyProperties(propertyId);
        if (!cancelled) {
          setNearbyProperties(result ?? []);
        }
      } catch (error) {
        // Catch silently, leave nearbyProperties as null
      }
    };

    fetchNearbyProperties();
    return () => { cancelled = true; };
  }, [propertyId]);

  // Track page view on mount
  useEffect(() => {
    if (!pdpInitData?.propertyDetails?.propertyId) return;
    trackPageMount({
      pageName: "commercial_pdp_view",
      eventCategory: "NEW-PDP",
      eventSource: "on page mount",
      eventLabel: buildEventLabel(pdpInitData),
      listingId: pdpInitData?.propertyDetails?.listingId,
      property_type: getPropertyType(pdpInitData?.propertyDetails?.commonAttributes?.propCategory),
      seller_id: pdpInitData?.propertyDetails?.sellerId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdpInitData?.propertyDetails?.propertyId]);

  return (
    <div className="commercial-pdp">
      {/* Section: Hero + Bid Card */}
      <div id="Overview">
        <CommercialHeroSection pdpCmsData={cmsData} />
      </div>

      {/* Section: Narrative Bar */}
      <section className="commercial-pdp__narrative">
        <div className="container">
          <NarrativeBar />
        </div>
      </section>

      {/* Section: Property Description & Executive Summary */}
      <section className="commercial-pdp__description" id="Description">
        <div className="container">
          <div className="commercial-pdp__description-layout">
            <div className="commercial-pdp__description-main">
              <PropertyDescription />
              {/* Property Details - below Investment Overview, beside sidebar */}
              <div id="PropertyDetails">
                <PropertyDetailsSection
                  pdpData={pdpInitData}
                  pdpPropertyFees={pdpPropertyFees}
                  emd={emd}
                  isFcOnlineFlag={isFcOnlineFlag}
                />
              </div>
            </div>
            <PropertySidebar
              openTourModalOnLoad={openTourModalOnLoad}
              onTourModalOnLoadConsumed={onTourModalOnLoadConsumed}
              onRegisterTourRequested={onRegisterTourRequested}
            />
          </div>
        </div>
      </section>

      {/* Section: Due Diligence Vault — hidden when no documents */}
      {propertyDocumentsInformation && Object.keys(propertyDocumentsInformation?.commercialDocumentList || {}).length > 0 && (
        <section className="commercial-pdp__documents" id="Documents">
          <div className="container">
            <DueDiligenceVault
              pdpDocuments={pdpDocuments}
              propertyDocumentsInformation={propertyDocumentsInformation}
              address={address}
              isNDASigned={pdpInitData?.isNDASigned}
            />
          </div>
        </section>
      )}

      {/* Section: Photo Gallery */}
      <section className="commercial-pdp__gallery" id="Gallery">
        <div className="container">
          <PropertyPhotoGallery imageDetails={pdpInitData?.propertyDetails?.imageDetails} />
        </div>
      </section>

      {/* Section: Location & Market Context */}
      {(pdpInitData?.propertyDetails?.detailedAttributes?.showMapStreetView === "Y" ||
        pdpInitData?.propertyDetails?.detailedAttributes?.showCommuterScoreLocHighlights === "Y") && (
        <section className="commercial-pdp__location" id="Location" style={{ borderTop: '1px solid #d1deea' }}>
          <div className="container">
            <LocationMarketContext
              pdpInitData={pdpInitData}
              cmsData={cmsData}
            />
          </div>
        </section>
      )}

      {/* Section: Broker Spotlight */}
      {pdpInitData?.listingAgentDetail && (
        <section className="commercial-pdp__brokers" id="Brokers">
          <div className="">
            <BrokerSpotlight
              listingAgentDetail={pdpInitData.listingAgentDetail}
              coListingAgentDetail={pdpInitData.coListingAgentDetail || null}
              auctioneerDetail={pdpInitData.listingAgentDetail?.auctioneerDetail || pdpInitData.auctioneerDetail || null}
              videoData={pdpInitData?.propertyDetails?.virtualTourDetailsList || null}
              agentDocumentDetail={pdpInitData?.propertyDocumentsInformation?.agentDocumentDetailList?.[0] || null}
              agentDocumentDetailList={pdpInitData?.propertyDocumentsInformation?.agentDocumentDetailList || []}
            />
          </div>
        </section>
      )}

      {/* Section: Fees & Disclaimers */}
      {pdpInitData && (
        <section className="commercial-pdp__fees" id="Fees">
          <div className="container">
            <h2 className="commercial-pdp__fees-heading">Fees & Property Information</h2>
            <div className="commercial-pdp__fees-card">
              <CommercialFeesContent
                pdpData={pdpInitData}
                pdpPropertyFees={pdpPropertyFees}
                emd={emd}
              />
            </div>
          </div>
        </section>
      )}

      {/* Section: Disclaimers — only render when data is loaded to avoid hooks error */}
      {disclaimer && disclaimer.length > 0 && (
        <section className="commercial-pdp__disclaimers" id="Disclaimers">
          <div className="container">
            <h2 className="commercial-pdp__disclaimers-heading">Important Disclaimers & Terms</h2>
            <div className="commercial-pdp__disclaimers-card">
              <div className="commercial-pdp__disclaimers-header">
                <img src="/assets/images/discailmer.png" alt="" className="commercial-pdp__disclaimers-header-icon" aria-hidden="true" />
                Please Read Carefully Before Bidding
              </div>
              <div className="commercial-pdp__disclaimers-body">
                <Disclaimer
                  data={disclaimer}
                  provisionalSaleDuration={provisionalSaleDuration}
                  cms={pdpDisclaimer}
                  pdpData={pdpInitData}
                />
              </div>
              <div className="commercial-pdp__disclaimers-footer">
                <span><strong>Questions?</strong> Please contact our support team or review our <a href="/about/terms" className="commercial-pdp__tc-link">Terms &amp; Conditions</a> for complete details.</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section: Auction Information */}
      <section className="commercial-pdp__auction-info" style={{paddingTop:48, paddingBottom:64}}>
        <div className="container">
          <AuctionInformation pdpInitData={pdpInitData} />
        </div>
      </section>

      {/* Section: FAQ */}
      {/* <CommercialFAQSection /> */}

      {/* Section: Banner Ads — driven by CMS pdpAds data */}
      {(() => {
        // Hide entire ads section if CMS flag showPdpAds is false
        if (!pdpAds?.showPdpAds) return null;
        // Extract enabled ads from CMS pdpAds object
        const adEntries = Object.entries(pdpAds)
              .filter(([key, val]) => !key.startsWith('@') && key !== 'showPdpAds' && val?.imgUrl && val?.enable !== 'N')
              .map(([, val]) => val);
        if (adEntries.length === 0) return null;
        return (
          <section className="commercial-pdp__ads">
            <div className={`commercial-pdp__ads-container${adEntries.length === 1 ? ' commercial-pdp__ads-container--single' : ''}`}>
              {adEntries.length === 1 ? (
                <div className="commercial-pdp__ads-slot" key={0}>
                  <a href={adEntries[0].redirectionURL || '#'} target="_blank" rel="noopener noreferrer">
                    <img src={adEntries[0].imgUrl} alt={adEntries[0].name || 'Advertisement'} />
                  </a>
                </div>
              ) : (
                // Carousel for 2+ ads with arrows
                <AdsCarousel ads={adEntries} />
              )}
            </div>
          </section>
        );
      })()}

      {/* Section: Recommended Properties */}
      {nearbyProperties && nearbyProperties.length > 0 && (
        <section className="commercial-pdp__recommended">
          <div className="container">
            <CommercialRecommendedProperties
              nearbyProperties={nearbyProperties}
              state={pdpInitData?.propertyDetails?.address?.state}
              propertyId={propertyId}
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default CommercialPDP;
